// src/pages/shop-settings-tabs/UnitsTab.jsx
// 单位管理这块逻辑（加载单位列表、切换启用状态）完全独立于其他tab的settings，
// 所以状态就放在这个组件自己内部，不塞进 useShopSettings 这个共用hook里
import React, { useState, useEffect } from 'react';
import { unitApi } from '../../api';

const UnitsTab = ({ language }) => {
  // 单位管理：租户不再自己创建/编辑单位，只在这里勾选/取消勾选要用哪些——
  // 用 getStats 而不是普通的 getList，多了个 product_count，方便商家看到
  // "这个单位现在有多少商品在用"，考虑要不要停用的时候心里有数
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [togglingUnitId, setTogglingUnitId] = useState(null);

  useEffect(() => { loadUnits(); }, []);

  const loadUnits = async () => {
    setLoadingUnits(true);
    try {
      const res = await unitApi.getStats();
      if (res.success) setUnits(res.data || []);
    } catch (err) {
      console.error('加载单位列表失败:', err);
    } finally {
      setLoadingUnits(false);
    }
  };

  // 按当前POS界面语言，取对应的单位名称——某个语言没填翻译，自动退回中文，
  // 不会出现空白
  const getUnitName = (unit) => {
    const fieldMap = { zh: 'name', en: 'name_en', km: 'name_km', ms: 'name_ms', zh_tw: 'name_zh_tw' };
    const field = fieldMap[language] || 'name';
    return unit[field] || unit.name;
  };

  const handleToggleUnit = async (unit) => {
    setTogglingUnitId(unit.id);
    try {
      const res = await unitApi.toggle(unit.id, { is_enabled: !unit.is_enabled });
      if (res.success) {
        setUnits(prev => prev.map(u => u.id === unit.id ? { ...u, is_enabled: !u.is_enabled } : u));
        if (res.message && res.message !== '设置已更新') {
          // 停用时如果后端提示"还有N个商品在用"，这里展示出来，不是静默切换
          alert(res.message);
        }
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '操作失败');
    } finally {
      setTogglingUnitId(null);
    }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
        <p>勾选要在商品编辑时使用的计量单位——单位本身（常用清单、特殊单位）由平台统一维护，这里只控制"用不用"，不需要自己新建或翻译。</p>
      </div>

      {loadingUnits ? (
        <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400 text-sm">加载中...</div>
      ) : units.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400 text-sm">暂无可用单位</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {units.map(unit => {
            const isBusy = togglingUnitId === unit.id;
            return (
              <div
                key={unit.id}
                className={`relative rounded-xl border p-4 transition-all ${
                  unit.is_enabled
                    ? 'border-blue-200 bg-blue-50/50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`text-base font-semibold truncate ${unit.is_enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                      {getUnitName(unit)}
                    </p>
                    {unit.product_count > 0 && (
                      <p className="text-xs text-gray-400 mt-1">{unit.product_count} 个商品在用</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleToggleUnit(unit)}
                    disabled={isBusy}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                      unit.is_enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        unit.is_enabled ? 'translate-x-4.5' : 'translate-x-1'
                      }`}
                      style={{ transform: unit.is_enabled ? 'translateX(18px)' : 'translateX(4px)' }}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UnitsTab;